import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { service } from "@ember/service";
import { on } from "@ember/modifier";
import { uniqueId } from "@ember/helper";
import type { ComponentLike } from "@glint/template";
import { t } from "ember-intl";
import type StorageService from "glesys-controlpanel/services/storage";
import mergeClasses from "glesys-controlpanel/modifiers/merge-classes";
import IconsX from "glesys-controlpanel/components/icons/x";

export interface AnnouncementSignature {
  Args: {
    /** Browser storage key, e.g. `glesys:block-storage-announcement-closed`. Closing the announcement sets it, and it stays hidden in that browser from then on. Without a key, Close only hides it until it renders again. */
    storageKey?: string;
    /** The announcement's heading. */
    title: string;
    /** A 16px icon shown before the title. */
    icon: ComponentLike<{ Element: SVGElement }>;
  };
  Blocks: {
    default: [];
    /** Calls to action, shown before Close. Clicking them doesn't hide the announcement. */
    actions: [];
  };
  Element: HTMLElement;
}

export default class Announcement extends Component<AnnouncementSignature> {
  @service declare storage: StorageService;

  headingId = uniqueId();

  @tracked isClosed = this.wasClosedBefore;

  get wasClosedBefore() {
    return this.args.storageKey ? this.storage.get(this.args.storageKey) === "true" : false;
  }

  close = () => {
    if (this.args.storageKey) {
      this.storage.set(this.args.storageKey, "true");
    }
    this.isClosed = true;
  };

  <template>
    {{#unless this.isClosed}}
      <section
        class="grid grid-cols-[auto_1fr_auto] sm:grid-cols-[auto_1fr_auto_auto] items-center gap-x-3 pt-4 px-4 pb-5 rounded-sm bg-indigo-100/40 dark:bg-indigo-400/15"
        aria-labelledby={{this.headingId}}
        data-test-announcement
        {{mergeClasses}}
        ...attributes
      >
        <@icon class="col-start-1 row-start-1 sm:self-start sm:mt-1 text-black dark:text-white" aria-hidden="true" />
        <div class="col-span-3 row-start-2 mt-2 max-w-160 sm:col-span-1 sm:col-start-2 sm:row-start-1 sm:mt-0">
          <h2 class="text-sm sm:text-base text-black dark:text-white" id={{this.headingId}}>{{@title}}</h2>
          <div class="mt-0.5 text-sm text-gray-600 dark:text-gray-400 text-balance">{{yield}}</div>
        </div>
        {{#if (has-block "actions")}}
          <div
            class="col-span-3 row-start-3 mt-3 flex items-center gap-3 sm:col-span-1 sm:col-start-3 sm:row-start-1 sm:mt-0 sm:ml-1"
          >
            {{yield to="actions"}}
          </div>
        {{/if}}
        <button
          class="col-start-3 row-start-1 justify-self-end sm:col-start-4 inline-flex items-center justify-center size-6 cursor-pointer rounded-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-hidden focus:ring-2 focus:ring-indigo-500"
          aria-label={{t "button.close"}}
          data-test-announcement-close
          type="button"
          {{on "click" this.close}}
        >
          <IconsX class="size-4 stroke-[1.25]" role="presentation" />
        </button>
      </section>
    {{/unless}}
  </template>
}
